import { WatchQueryFetchPolicy } from '@apollo/client';

import { useStudentQuery } from 'lib/apollo/student-apollo-hooks';
import { GET_STUDENT_LESSON_ASSIGNMENT_BY_ID } from '../get-student-lesson-assignment-by-id.graphql';
import {
  ActivitiesFontSize,
  LANGUAGE_CODE_EN_AU,
  LanguageCodes,
} from 'global/constants';
import { useActivityBuilderTheme } from '../../activity-builder/theme/activity-builder-theme';
import {
  StudentAssignmentLessonLinkedContent,
  StudentLessonActivities,
} from '../student-lesson.constants';
import type {
  GetStudentLessonAssignmentByIdQuery,
  GetStudentLessonAssignmentByIdQueryVariables,
} from '../get-student-lesson-assignment-by-id.graphql.generated';

type GetStudentLessonAssignmentByIdProps = {
  assignmentId: number;
  classId: number | undefined;
  language?: LanguageCodes;
  fetchPolicy?: WatchQueryFetchPolicy;
  skip?: boolean;
};

export const useGetStudentLessonAssignmentById = ({
  assignmentId,
  classId,
  language,
  fetchPolicy = 'cache-first',
  skip,
}: GetStudentLessonAssignmentByIdProps) => {
  const { updateFontSize } = useActivityBuilderTheme();

  const { data, loading, error } = useStudentQuery<
    GetStudentLessonAssignmentByIdQuery,
    GetStudentLessonAssignmentByIdQueryVariables
  >(GET_STUDENT_LESSON_ASSIGNMENT_BY_ID, {
    skip: skip || !assignmentId || !classId,
    fetchPolicy,
    variables: {
      input: {
        assignmentId,
        classId: classId!,
        language: language ?? LANGUAGE_CODE_EN_AU,
      },
    },
    onCompleted: res => {
      const activities = res.getStudentAssignmentById.assignedActivities;
      if (activities?.__typename === 'LessonActivities' && activities.styles) {
        updateFontSize(activities.styles.fontSize as ActivitiesFontSize);
      }
    },
  });

  const assignedActivities = data?.getStudentAssignmentById?.assignedActivities;

  return {
    assignment: data?.getStudentAssignmentById,
    assignedActivities: assignedActivities as
      | StudentLessonActivities
      | undefined,
    linkedContent: data?.getStudentAssignmentById?.linkedContent as
      | StudentAssignmentLessonLinkedContent
      | undefined,
    loading,
    error,
  };
};
